// @flow
import type { Element } from 'react';

import React from 'react';
import { Form, FormText } from 'reactstrap';
import {
  ALLOW_FIRE_WATCH_TO_BE_SUFFICIENT,
  ALLOW_TEMPORARY_REPAIRS,
  ALLOW_TEMPORARY_SHORING,
  BUILDING_REPLACEMENT_TIME,
  CASH_ON_HAND,
  DAMAGE_CORRELATION_MODEL,
  DOOR_RACKING_AFFECTS_EGRESS,
  EQUIPMENT_PREQUALIFIED,
  INCLUDE_FLOODING_IMPACT,
  INCLUDE_IMPEDANCE_CONTRACTOR,
  INCLUDE_IMPEDANCE_ENGINEERING,
  INCLUDE_IMPEDANCE_FINANCING,
  INCLUDE_IMPEDANCE_INSPECTION,
  INCLUDE_IMPEDANCE_LONG_LEAD_TIME,
  INCLUDE_IMPEDANCE_PERMITTING,
  INCLUDE_SURGE_DEMAND,
  IS_DENSE_URBAN_AREA,
  MAX_WALKABLE_STORY,
  MINIMUM_NUMBER_OF_EXITS_REQUIRED,
  MITIGATION_CONTRACTOR,
  MITIGATION_ENGINEER,
  MITIGATION_FUNDING_SOURCE,
  MITIGATION_INSPECTOR,
  NEED_COOLING_FOR_FUNCTION,
  NEED_COOLING_FOR_REOCCUPANCY,
  NEED_ELECTRICAL_POWER_FOR_FUNCTION,
  NEED_ELECTRICAL_POWER_FOR_REOCCUPANCY,
  NEED_ELEVATOR_FOR_FUNCTION,
  NEED_EXHAUST_FOR_FUNCTION,
  NEED_EXHAUST_FOR_REOCCUPANCY,
  NEED_HEATING_FOR_FUNCTION,
  NEED_HEATING_FOR_REOCCUPANCY,
  NEED_POTABLE_WATER_FOR_FUNCTION,
  NEED_POTABLE_WATER_FOR_REOCCUPANCY,
  NEED_SANITATION_PLUMBING_FOR_FUNCTION,
  NEED_SANITATION_PLUMBING_FOR_REOCCUPANCY,
  NEED_VENTILATION_FOR_FUNCTION,
  NEED_VENTILATION_FOR_REOCCUPANCY,
  PERCENT_OF_EXITS_REQUIRED,
  PREQUALIFIED_ACC_GROUND,
  PREQUALIFIED_ACC_ROOF,
  PREQUALIFIED_DESIGN_CODE_YEAR,
  REPAIR_TIME_METHOD,
  USE_SMART_EQUIPMENT_COMPONENTS,
  USE_SMART_PIPING_COMPONENTS,
  USE_UPDATED_ELEVATOR_COMPONENTS,
} from '@hbrisk/sp3-risk-model-support/models/app/attributes/names/index.js';
import BackButton from '#components/pages/Models/Model/sections/support/buttons/BackButton.jsx';
import NextButton from '#components/pages/Models/Model/sections/support/buttons/NextButton.jsx';
import Tip from '#components/support/tooltips/Tip/index.jsx';
import {
  Field,
  FormGroup,
  Fieldset,
  FormGroupCol as Col,
  FormGroupRow as Row,
} from '#components/pages/Models/Model/support/form/index.js';

type Props = {|
  fieldsByName: Object,
  form: string,
  handleSubmit: Function,
  heading: string,
  onBack: Function,
  onNext: Function,
  showIncludeImpedanceLongLeadTimeWarning: boolean,
|};

const RepairTimeOptionsSection = (props: Props): Element<'div'> => {
  const {
    fieldsByName,
    handleSubmit,
    heading,
    form,
    onBack,
    onNext,
    showIncludeImpedanceLongLeadTimeWarning,
  } = props;

  return (
    <div>
      <h2>{heading}</h2>
      <Form noValidate onSubmit={handleSubmit}>
        <FormGroup form={form} fields={[REPAIR_TIME_METHOD]} row>
          <Col lg="6">
            <Field field={fieldsByName[REPAIR_TIME_METHOD]} />
          </Col>
        </FormGroup>
        <Fieldset>
          <legend>General</legend>
          <FormGroup form={form} fields={[BUILDING_REPLACEMENT_TIME]} row>
            <Col lg="4">
              <Field field={fieldsByName[BUILDING_REPLACEMENT_TIME]} />
            </Col>
          </FormGroup>
          <FormGroup form={form} fields={[DAMAGE_CORRELATION_MODEL]} row>
            <Col lg="6">
              <Field field={fieldsByName[DAMAGE_CORRELATION_MODEL]} />
            </Col>
          </FormGroup>
        </Fieldset>
        <Fieldset>
          <legend>
            Factors Delaying the Start of Repairs <Tip tipId="factorsDelayingTheStartOfRepairs" />
          </legend>
          <FormGroup form={form} fields={[INCLUDE_IMPEDANCE_INSPECTION]} row>
            <Col lg="6">
              <Field field={fieldsByName[INCLUDE_IMPEDANCE_INSPECTION]} />
            </Col>
          </FormGroup>
          <FormGroup form={form} fields={[INCLUDE_IMPEDANCE_FINANCING]} row>
            <Col lg="6">
              <Field field={fieldsByName[INCLUDE_IMPEDANCE_FINANCING]} />
            </Col>
          </FormGroup>
          <FormGroup form={form} fields={[INCLUDE_IMPEDANCE_PERMITTING]} row>
            <Col lg="6">
              <Field field={fieldsByName[INCLUDE_IMPEDANCE_PERMITTING]} />
            </Col>
          </FormGroup>
          <FormGroup form={form} fields={[INCLUDE_IMPEDANCE_ENGINEERING]} row>
            <Col lg="6">
              <Field field={fieldsByName[INCLUDE_IMPEDANCE_ENGINEERING]} />
            </Col>
          </FormGroup>
          <FormGroup form={form} fields={[INCLUDE_IMPEDANCE_CONTRACTOR]} row>
            <Col lg="6">
              <Field field={fieldsByName[INCLUDE_IMPEDANCE_CONTRACTOR]} />
            </Col>
          </FormGroup>
          <FormGroup form={form} fields={[INCLUDE_IMPEDANCE_LONG_LEAD_TIME]}>
            <Row>
              <Col lg="6">
                <Field field={fieldsByName[INCLUDE_IMPEDANCE_LONG_LEAD_TIME]} />
              </Col>
            </Row>
            {showIncludeImpedanceLongLeadTimeWarning && (
              <FormText
                className="mt-0 pt-0 pl-4"
                id="includeImpedanceLongLeadTimeWarning"
                color="muted"
              >
                * The official ATC-138 recommendation is to include Long Lead Time
              </FormText>
            )}
          </FormGroup>
        </Fieldset>

        <Fieldset>
          <legend>
            Mitigation Factors <Tip tipId="mitigationFactors" />
          </legend>
          <FormGroup form={form} fields={[MITIGATION_INSPECTOR]} row>
            <Col lg="6">
              <Field field={fieldsByName[MITIGATION_INSPECTOR]} />
            </Col>
          </FormGroup>
          <FormGroup form={form} fields={[MITIGATION_ENGINEER]} row>
            <Col lg="6">
              <Field field={fieldsByName[MITIGATION_ENGINEER]} />
            </Col>
          </FormGroup>
          <FormGroup form={form} fields={[MITIGATION_CONTRACTOR]} row>
            <Col lg="6">
              <Field field={fieldsByName[MITIGATION_CONTRACTOR]} />
            </Col>
          </FormGroup>
          <FormGroup form={form} fields={[MITIGATION_FUNDING_SOURCE]} row>
            <Col lg="6">
              <Field field={fieldsByName[MITIGATION_FUNDING_SOURCE]} />
            </Col>
          </FormGroup>
          <FormGroup form={form} fields={[CASH_ON_HAND]} row>
            <Col lg="4">
              <Field field={fieldsByName[CASH_ON_HAND]} />
            </Col>
          </FormGroup>
        </Fieldset>
        <Fieldset
          id="atc138FunctionalRecoveryOptions"
          form={form}
          fields={[
            NEED_VENTILATION_FOR_REOCCUPANCY,
            NEED_VENTILATION_FOR_FUNCTION,
            NEED_COOLING_FOR_REOCCUPANCY,
            NEED_COOLING_FOR_FUNCTION,
            NEED_HEATING_FOR_REOCCUPANCY,
            NEED_HEATING_FOR_FUNCTION,
            NEED_EXHAUST_FOR_REOCCUPANCY,
            NEED_EXHAUST_FOR_FUNCTION,
            NEED_POTABLE_WATER_FOR_REOCCUPANCY,
            NEED_POTABLE_WATER_FOR_FUNCTION,
            NEED_SANITATION_PLUMBING_FOR_REOCCUPANCY,
            NEED_SANITATION_PLUMBING_FOR_FUNCTION,
            NEED_ELECTRICAL_POWER_FOR_REOCCUPANCY,
            NEED_ELECTRICAL_POWER_FOR_FUNCTION,
            NEED_ELEVATOR_FOR_FUNCTION,
            MAX_WALKABLE_STORY,
            USE_UPDATED_ELEVATOR_COMPONENTS,
            ALLOW_TEMPORARY_REPAIRS,
            ALLOW_TEMPORARY_SHORING,
            ALLOW_FIRE_WATCH_TO_BE_SUFFICIENT,
            PERCENT_OF_EXITS_REQUIRED,
            MINIMUM_NUMBER_OF_EXITS_REQUIRED,
            DOOR_RACKING_AFFECTS_EGRESS,
            USE_SMART_PIPING_COMPONENTS,
            USE_SMART_EQUIPMENT_COMPONENTS,
            EQUIPMENT_PREQUALIFIED,
            PREQUALIFIED_DESIGN_CODE_YEAR,
            PREQUALIFIED_ACC_GROUND,
            PREQUALIFIED_ACC_ROOF,
            INCLUDE_FLOODING_IMPACT,
            INCLUDE_SURGE_DEMAND,
            IS_DENSE_URBAN_AREA,
          ]}
        >
          <legend>ATC-138 Functional Recovery (Beta) Options</legend>
          <Fieldset
            form={form}
            fields={[
              NEED_VENTILATION_FOR_REOCCUPANCY,
              NEED_VENTILATION_FOR_FUNCTION,
              NEED_COOLING_FOR_REOCCUPANCY,
              NEED_COOLING_FOR_FUNCTION,
              NEED_HEATING_FOR_REOCCUPANCY,
              NEED_HEATING_FOR_FUNCTION,
              NEED_EXHAUST_FOR_REOCCUPANCY,
              NEED_EXHAUST_FOR_FUNCTION,
            ]}
          >
            <legend>HVAC</legend>
            <FormGroup
              form={form}
              fields={[NEED_VENTILATION_FOR_REOCCUPANCY, NEED_VENTILATION_FOR_FUNCTION]}
            >
              <Row>
                <Col lg="4" className="font-weight-bold">
                  Ventilation
                </Col>
              </Row>
              <FormGroup form={form} fields={[NEED_VENTILATION_FOR_REOCCUPANCY]} row>
                <Col lg="4" className="ml-3">
                  <Field field={fieldsByName[NEED_VENTILATION_FOR_REOCCUPANCY]} />
                </Col>
              </FormGroup>
              <FormGroup form={form} fields={[NEED_VENTILATION_FOR_FUNCTION]} row>
                <Col lg="4" className="ml-3">
                  <Field field={fieldsByName[NEED_VENTILATION_FOR_FUNCTION]} />
                </Col>
              </FormGroup>
            </FormGroup>
            <FormGroup
              form={form}
              fields={[NEED_COOLING_FOR_REOCCUPANCY, NEED_COOLING_FOR_FUNCTION]}
            >
              <Row>
                <Col lg="4" className="font-weight-bold">
                  Cooling
                </Col>
              </Row>
              <FormGroup form={form} fields={[NEED_COOLING_FOR_REOCCUPANCY]} row>
                <Col lg="4" className="ml-3">
                  <Field field={fieldsByName[NEED_COOLING_FOR_REOCCUPANCY]} />
                </Col>
              </FormGroup>
              <FormGroup form={form} fields={[NEED_COOLING_FOR_FUNCTION]} row>
                <Col lg="4" className="ml-3">
                  <Field field={fieldsByName[NEED_COOLING_FOR_FUNCTION]} />
                </Col>
              </FormGroup>
            </FormGroup>
            <FormGroup
              form={form}
              fields={[NEED_HEATING_FOR_REOCCUPANCY, NEED_HEATING_FOR_FUNCTION]}
            >
              <Row>
                <Col lg="4" className="font-weight-bold">
                  Heating
                </Col>
              </Row>
              <FormGroup form={form} fields={[NEED_HEATING_FOR_REOCCUPANCY]} row>
                <Col lg="4" className="ml-3">
                  <Field field={fieldsByName[NEED_HEATING_FOR_REOCCUPANCY]} />
                </Col>
              </FormGroup>
              <FormGroup form={form} fields={[NEED_HEATING_FOR_FUNCTION]} row>
                <Col lg="4" className="ml-3">
                  <Field field={fieldsByName[NEED_HEATING_FOR_FUNCTION]} />
                </Col>
              </FormGroup>
            </FormGroup>
            <FormGroup
              form={form}
              fields={[NEED_EXHAUST_FOR_REOCCUPANCY, NEED_EXHAUST_FOR_FUNCTION]}
            >
              <Row>
                <Col lg="4" className="font-weight-bold">
                  Exhaust
                </Col>
              </Row>
              <FormGroup form={form} fields={[NEED_EXHAUST_FOR_REOCCUPANCY]} row>
                <Col lg="4" className="ml-3">
                  <Field field={fieldsByName[NEED_EXHAUST_FOR_REOCCUPANCY]} />
                </Col>
              </FormGroup>
              <FormGroup form={form} fields={[NEED_EXHAUST_FOR_FUNCTION]} row>
                <Col lg="4" className="ml-3">
                  <Field field={fieldsByName[NEED_EXHAUST_FOR_FUNCTION]} />
                </Col>
              </FormGroup>
            </FormGroup>
          </Fieldset>
          <Fieldset
            form={form}
            fields={[
              NEED_POTABLE_WATER_FOR_REOCCUPANCY,
              NEED_POTABLE_WATER_FOR_FUNCTION,
              NEED_SANITATION_PLUMBING_FOR_REOCCUPANCY,
              NEED_SANITATION_PLUMBING_FOR_FUNCTION,
            ]}
          >
            <legend>Plumbing</legend>
            <FormGroup
              form={form}
              fields={[NEED_POTABLE_WATER_FOR_REOCCUPANCY, NEED_POTABLE_WATER_FOR_FUNCTION]}
            >
              <Row>
                <Col lg="4" className="font-weight-bold">
                  Potable Water
                </Col>
              </Row>
              <FormGroup form={form} fields={[NEED_POTABLE_WATER_FOR_REOCCUPANCY]} row>
                <Col lg="4" className="ml-3">
                  <Field field={fieldsByName[NEED_POTABLE_WATER_FOR_REOCCUPANCY]} />
                </Col>
              </FormGroup>
              <FormGroup form={form} fields={[NEED_POTABLE_WATER_FOR_FUNCTION]} row>
                <Col lg="4" className="ml-3">
                  <Field field={fieldsByName[NEED_POTABLE_WATER_FOR_FUNCTION]} />
                </Col>
              </FormGroup>
            </FormGroup>
            <FormGroup
              form={form}
              fields={[
                NEED_SANITATION_PLUMBING_FOR_REOCCUPANCY,
                NEED_SANITATION_PLUMBING_FOR_FUNCTION,
              ]}
            >
              <Row>
                <Col lg="4" className="font-weight-bold">
                  Sanitation Plumbing
                </Col>
              </Row>
              <FormGroup form={form} fields={[NEED_SANITATION_PLUMBING_FOR_REOCCUPANCY]} row>
                <Col lg="4" className="ml-3">
                  <Field field={fieldsByName[NEED_SANITATION_PLUMBING_FOR_REOCCUPANCY]} />
                </Col>
              </FormGroup>
              <FormGroup form={form} fields={[NEED_SANITATION_PLUMBING_FOR_FUNCTION]} row>
                <Col lg="4" className="ml-3">
                  <Field field={fieldsByName[NEED_SANITATION_PLUMBING_FOR_FUNCTION]} />
                </Col>
              </FormGroup>
            </FormGroup>
          </Fieldset>
          <Fieldset
            form={form}
            fields={[NEED_ELECTRICAL_POWER_FOR_REOCCUPANCY, NEED_ELECTRICAL_POWER_FOR_FUNCTION]}
          >
            <legend>Electrical</legend>
            <FormGroup
              form={form}
              fields={[NEED_ELECTRICAL_POWER_FOR_REOCCUPANCY, NEED_ELECTRICAL_POWER_FOR_FUNCTION]}
            >
              <Row>
                <Col lg="4" className="font-weight-bold">
                  Electrical Power
                </Col>
              </Row>
              <FormGroup form={form} fields={[NEED_ELECTRICAL_POWER_FOR_REOCCUPANCY]} row>
                <Col lg="4" className="ml-3">
                  <Field field={fieldsByName[NEED_ELECTRICAL_POWER_FOR_REOCCUPANCY]} />
                </Col>
              </FormGroup>
              <FormGroup form={form} fields={[NEED_ELECTRICAL_POWER_FOR_FUNCTION]} row>
                <Col lg="4" className="ml-3">
                  <Field field={fieldsByName[NEED_ELECTRICAL_POWER_FOR_FUNCTION]} />
                </Col>
              </FormGroup>
            </FormGroup>
          </Fieldset>
          <Fieldset
            form={form}
            fields={[
              NEED_ELEVATOR_FOR_FUNCTION,
              MAX_WALKABLE_STORY,
              USE_UPDATED_ELEVATOR_COMPONENTS,
            ]}
          >
            <legend>Elevator</legend>
            <FormGroup form={form} fields={[NEED_ELEVATOR_FOR_FUNCTION]} row>
              <Col lg="6">
                <Field field={fieldsByName[NEED_ELEVATOR_FOR_FUNCTION]} />
              </Col>
            </FormGroup>
            <FormGroup form={form} fields={[MAX_WALKABLE_STORY]} row>
              <Col lg="6">
                <Field field={fieldsByName[MAX_WALKABLE_STORY]} />
              </Col>
            </FormGroup>
            <FormGroup form={form} fields={[USE_UPDATED_ELEVATOR_COMPONENTS]} row>
              <Col lg="6">
                <Field field={fieldsByName[USE_UPDATED_ELEVATOR_COMPONENTS]} />
              </Col>
            </FormGroup>
          </Fieldset>
          <Fieldset form={form} fields={[ALLOW_TEMPORARY_REPAIRS, ALLOW_TEMPORARY_SHORING]}>
            <legend>Temporary Repairs</legend>
            <FormGroup form={form} fields={[ALLOW_TEMPORARY_REPAIRS]} row>
              <Col lg="6">
                <Field field={fieldsByName[ALLOW_TEMPORARY_REPAIRS]} />
              </Col>
            </FormGroup>
            <FormGroup form={form} fields={[ALLOW_TEMPORARY_SHORING]} row>
              <Col lg="6">
                <Field field={fieldsByName[ALLOW_TEMPORARY_SHORING]} />
              </Col>
            </FormGroup>
          </Fieldset>
          <Fieldset form={form} fields={[ALLOW_FIRE_WATCH_TO_BE_SUFFICIENT]}>
            <legend>Fire</legend>
            <FormGroup form={form} fields={[ALLOW_FIRE_WATCH_TO_BE_SUFFICIENT]} row>
              <Col lg="6">
                <Field field={fieldsByName[ALLOW_FIRE_WATCH_TO_BE_SUFFICIENT]} />
              </Col>
            </FormGroup>
          </Fieldset>
          <Fieldset
            form={form}
            fields={[
              PERCENT_OF_EXITS_REQUIRED,
              MINIMUM_NUMBER_OF_EXITS_REQUIRED,
              DOOR_RACKING_AFFECTS_EGRESS,
            ]}
          >
            <legend>Egress</legend>
            <FormGroup form={form} fields={[PERCENT_OF_EXITS_REQUIRED]} row>
              <Col lg="6">
                <Field field={fieldsByName[PERCENT_OF_EXITS_REQUIRED]} />
              </Col>
            </FormGroup>
            <FormGroup form={form} fields={[MINIMUM_NUMBER_OF_EXITS_REQUIRED]} row>
              <Col lg="6">
                <Field field={fieldsByName[MINIMUM_NUMBER_OF_EXITS_REQUIRED]} />
              </Col>
            </FormGroup>
            <FormGroup form={form} fields={[DOOR_RACKING_AFFECTS_EGRESS]} row>
              <Col lg="6">
                <Field field={fieldsByName[DOOR_RACKING_AFFECTS_EGRESS]} />
              </Col>
            </FormGroup>
          </Fieldset>
          <Fieldset
            form={form}
            fields={[
              USE_SMART_PIPING_COMPONENTS,
              USE_SMART_EQUIPMENT_COMPONENTS,
              EQUIPMENT_PREQUALIFIED,
              PREQUALIFIED_DESIGN_CODE_YEAR,
              PREQUALIFIED_ACC_GROUND,
              PREQUALIFIED_ACC_ROOF,
            ]}
          >
            <legend>Smart Components</legend>
            <FormGroup form={form} fields={[USE_SMART_PIPING_COMPONENTS]} row>
              <Col lg="4">
                <Field field={fieldsByName[USE_SMART_PIPING_COMPONENTS]} />
              </Col>
            </FormGroup>
            <FormGroup form={form} fields={[USE_SMART_EQUIPMENT_COMPONENTS]} row>
              <Col lg="4">
                <Field field={fieldsByName[USE_SMART_EQUIPMENT_COMPONENTS]} />
              </Col>
            </FormGroup>
            <FormGroup form={form} fields={[EQUIPMENT_PREQUALIFIED]} row>
              <Col lg="4">
                <Field field={fieldsByName[EQUIPMENT_PREQUALIFIED]} />
              </Col>
            </FormGroup>
            <FormGroup form={form} fields={[PREQUALIFIED_DESIGN_CODE_YEAR]} row>
              <Col lg="4">
                <Field field={fieldsByName[PREQUALIFIED_DESIGN_CODE_YEAR]} />
              </Col>
            </FormGroup>
            <FormGroup form={form} fields={[PREQUALIFIED_ACC_GROUND]} row>
              <Col lg="4">
                <Field field={fieldsByName[PREQUALIFIED_ACC_GROUND]} />
              </Col>
            </FormGroup>
            <FormGroup form={form} fields={[PREQUALIFIED_ACC_ROOF]} row>
              <Col lg="4">
                <Field field={fieldsByName[PREQUALIFIED_ACC_ROOF]} />
              </Col>
            </FormGroup>
          </Fieldset>
          <Fieldset form={form} fields={[INCLUDE_FLOODING_IMPACT]}>
            <legend>Flooding</legend>
            <FormGroup form={form} fields={[INCLUDE_FLOODING_IMPACT]} row>
              <Col lg="4">
                <Field field={fieldsByName[INCLUDE_FLOODING_IMPACT]} />
              </Col>
            </FormGroup>
          </Fieldset>
          <Fieldset form={form} fields={[INCLUDE_SURGE_DEMAND, IS_DENSE_URBAN_AREA]}>
            <legend>Surge Demand</legend>
            <FormGroup form={form} fields={[INCLUDE_SURGE_DEMAND]} row>
              <Col lg="4">
                <Field field={fieldsByName[INCLUDE_SURGE_DEMAND]} />
              </Col>
            </FormGroup>
            <FormGroup form={form} fields={[IS_DENSE_URBAN_AREA]} row>
              <Col lg="4">
                <Field field={fieldsByName[IS_DENSE_URBAN_AREA]} />
              </Col>
            </FormGroup>
          </Fieldset>
        </Fieldset>
        <BackButton onClick={onBack} />
        <NextButton onClick={onNext} />
      </Form>
    </div>
  );
};

export default RepairTimeOptionsSection;
